if (!customElements.get('fnd-custom-discount')) {
  class FndCustomDiscount extends HTMLElement {
    connectedCallback() {
      // Defer initialization to ensure DOM is ready
      setTimeout(() => this.initialize(), 50);
    }

    initialize() {
      this.buildVariantMap();
      this.allowedVariants = this.getAllowedVariants();
      this.currentVariantId = this.dataset.currentVariant;
      this.radioButtons = this.querySelectorAll('.product__discount-radio');
      this.isInitialized = false;

      console.log('FndCustomDiscount: Initialized');
      console.log('FndCustomDiscount: Current variant ID', this.currentVariantId);
      console.log('FndCustomDiscount: Variant map', this.variantIdToTitle);
      console.log('FndCustomDiscount: Allowed variants', this.allowedVariants);

      // Find the product form - try multiple methods
      this.productForm = this.closest('form') ||
                        this.closest('.product-details-wrapper')?.querySelector('form[data-product-form]') ||
                        this.closest('.product-area')?.querySelector('form[data-product-form]') ||
                        document.querySelector('form[data-product-form]');

      if (!this.productForm) {
        console.error('FndCustomDiscount: Could not find product form');
        return;
      }

      console.log('FndCustomDiscount: Found form', this.productForm);

      // Check if current variant is allowed
      if (!this.isVariantAllowed(this.currentVariantId)) {
        console.log('FndCustomDiscount: Current variant not allowed, hiding widget');
        this.hideWidget();
        this.setupVariantListener();
        return;
      }

      // Initialize the widget for allowed variant
      this.showWidget();
      this.initializeWidget();
      this.setupVariantListener();
    }

    buildVariantMap() {
      try {
        const mapData = this.dataset.variantMap;
        this.variantMap = mapData ? JSON.parse(mapData) : [];
        // Create reverse lookup: ID -> Title
        this.variantIdToTitle = {};
        this.variantMap.forEach(v => {
          this.variantIdToTitle[v.id] = v.title;
        });
      } catch (e) {
        console.error('FndCustomDiscount: Failed to parse variant map', e);
        this.variantMap = [];
        this.variantIdToTitle = {};
      }
    }

    getAllowedVariants() {
      const allowedStr = this.dataset.allowedVariants || '';
      console.log('FndCustomDiscount: Raw allowed variants string:', allowedStr);
      if (!allowedStr.trim()) {
        // If no variants specified, allow all
        return null;
      }
      // Parse comma-separated variant names (case-insensitive)
      // Filter out empty strings from trailing commas
      const parsed = allowedStr.split(',')
        .map(name => name.trim().toLowerCase())
        .filter(name => name.length > 0);
      console.log('FndCustomDiscount: Parsed allowed variants:', parsed);
      return parsed.length > 0 ? parsed : null;
    }

    isVariantAllowed(variantId) {
      // If no restrictions, allow all
      if (!this.allowedVariants) {
        console.log('FndCustomDiscount: No restrictions, allowing all variants');
        return true;
      }
      // Get the variant title for this ID
      const variantTitle = this.variantIdToTitle[variantId];
      if (!variantTitle) {
        console.warn('FndCustomDiscount: Unknown variant ID', variantId);
        return false;
      }
      // Check if variant title matches any allowed name (case-insensitive)
      const isAllowed = this.allowedVariants.some(allowed =>
        variantTitle.toLowerCase().includes(allowed) || allowed.includes(variantTitle.toLowerCase())
      );
      console.log(`FndCustomDiscount: Checking "${variantTitle}" against allowed list:`, isAllowed);
      return isAllowed;
    }

    hideWidget() {
      this.classList.add('product__discount--hidden');

      // Uncheck all radio buttons and remove selected class
      this.radioButtons.forEach(radio => {
        radio.checked = false;
        radio.closest('.product__discount-option')?.classList.remove('product__discount-option--selected');
      });

      // Reset data attributes
      this.dataset.discountActive = 'false';
      this.dataset.discountQuantity = '1';
      this.dataset.discountPercentage = '0';

      // Reset quantity to 1 when hiding
      if (this.productForm) {
        const quantityInput = this.productForm.querySelector('input[name="quantity"]');
        if (quantityInput) {
          console.log('FndCustomDiscount: Resetting quantity to 1');
          quantityInput.value = 1;
          quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
          quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
      this.updateStickyAtcQuantity(1);

      // Clean up if previously initialized
      if (this.isInitialized && this.discountProperty) {
        this.discountProperty.remove();
        this.discountProperty = null;
      }
      this.isInitialized = false;
    }

    showWidget() {
      this.classList.remove('product__discount--hidden');
    }

    initializeWidget() {
      if (this.isInitialized) return;

      // Create and inject the hidden input into the form
      this.discountProperty = document.createElement('input');
      this.discountProperty.type = 'hidden';
      this.discountProperty.name = 'properties[_fnd-volumetric-discount]';
      this.discountProperty.value = '';
      this.productForm.appendChild(this.discountProperty);

      // Remove any existing event listeners by cloning radio buttons
      this.radioButtons.forEach(radio => {
        radio.addEventListener('change', this.handleOptionChange.bind(this));
      });

      // Select first option by default
      if (this.radioButtons.length > 0) {
        const firstRadio = this.radioButtons[0];
        firstRadio.checked = true;
        firstRadio.closest('.product__discount-option')?.classList.add('product__discount-option--selected');

        // Set initial discount value (percentage)
        const discount = firstRadio.dataset.discount;
        if (this.discountProperty) {
          this.discountProperty.value = discount;
        }

        // Set data attributes for sticky ATC
        const quantity = parseInt(firstRadio.dataset.quantity) || 1;
        this.dataset.discountActive = 'true';
        this.dataset.discountQuantity = quantity;
        this.dataset.discountPercentage = discount;

        // Set the quantity for the first option
        this.updateQuantity(firstRadio);
        console.log('FndCustomDiscount: Initialized widget with first option');
      }

      this.isInitialized = true;
    }

    setupVariantListener() {
      // Listen for variant changes on option-value radio buttons
      const optionInputs = document.querySelectorAll('.option-value-input');
      if (optionInputs.length > 0) {
        optionInputs.forEach(input => {
          input.addEventListener('change', () => {
            // Wait a moment for the variant to be selected
            setTimeout(() => {
              const newVariantId = this.getCurrentVariantId();
              if (newVariantId) {
                this.handleVariantChange(newVariantId);
              }
            }, 100);
          });
        });
      }

      // Fallback: Listen for hidden variant select (if it exists)
      if (this.productForm) {
        const variantSelect = this.productForm.querySelector('[name="id"]');
        if (variantSelect) {
          variantSelect.addEventListener('change', (e) => {
            this.handleVariantChange(e.target.value);
          });
        }
      }

      // Also listen for custom variant change events (common in Shopify themes)
      document.addEventListener('variant:change', (e) => {
        if (e.detail && e.detail.variant) {
          this.handleVariantChange(e.detail.variant.id);
        }
      });
    }

    getCurrentVariantId() {
      // Try to get from hidden select first
      const variantSelect = this.productForm?.querySelector('[name="id"]');
      if (variantSelect && variantSelect.value) {
        return variantSelect.value;
      }

      // Otherwise try to find from product data in the page
      const productData = document.querySelector('[data-section-data]');
      if (productData) {
        try {
          const data = JSON.parse(productData.textContent);
          if (data.product && data.product.selected_or_first_available_variant) {
            return String(data.product.selected_or_first_available_variant.id);
          }
        } catch (e) {
          console.error('FndCustomDiscount: Failed to parse product data', e);
        }
      }

      return this.currentVariantId;
    }

    handleVariantChange(newVariantId) {
      console.log('FndCustomDiscount: Variant changed to', newVariantId);

      // Get variant title for logging
      const variantTitle = this.variantIdToTitle[newVariantId];
      console.log('FndCustomDiscount: Variant title is', variantTitle);
      console.log('FndCustomDiscount: Allowed variants are', this.allowedVariants);

      this.currentVariantId = String(newVariantId);

      if (this.isVariantAllowed(this.currentVariantId)) {
        console.log('FndCustomDiscount: Variant allowed, showing widget');
        // First show the widget
        this.showWidget();
        // Then initialize (will select first option and set quantity)
        this.initializeWidget();
      } else {
        console.log('FndCustomDiscount: Variant not allowed, hiding widget');
        // Hide widget and reset quantity to 1
        this.hideWidget();
      }
    }

    handleOptionChange(event) {
      const radio = event.target;

      // Update visual selection
      this.querySelectorAll('.product__discount-option').forEach(option => {
        option.classList.remove('product__discount-option--selected');
      });
      radio.closest('.product__discount-option').classList.add('product__discount-option--selected');

      // Update line property (discount percentage)
      const discount = radio.dataset.discount;
      if (this.discountProperty) {
        this.discountProperty.value = discount;
      }

      // Update data attributes for sticky ATC
      const quantity = parseInt(radio.dataset.quantity) || 1;
      this.dataset.discountActive = 'true';
      this.dataset.discountQuantity = quantity;
      this.dataset.discountPercentage = discount;

      // Update quantity selector
      this.updateQuantity(radio);
    }

    updateQuantity(radio) {
      const quantity = parseInt(radio.dataset.quantity) || 1;

      if (!this.productForm) {
        console.error('FndCustomDiscount: Product form not available');
        return;
      }

      // Find and update quantity input
      const quantityInput = this.productForm.querySelector('input[name="quantity"]');
      if (quantityInput) {
        console.log('FndCustomDiscount: Setting quantity to', quantity);
        quantityInput.value = quantity;
        // Trigger change event for any listeners
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      } else {
        console.error('FndCustomDiscount: Could not find quantity input in form');
      }

      this.updateStickyAtcQuantity(quantity);
    }

    updateStickyAtcQuantity(quantity) {
      const stickyQtyEls = document.querySelectorAll('.zrx-sticky-atc-product-qty-amount');
      if (!stickyQtyEls.length) {
        return;
      }

      stickyQtyEls.forEach((el) => {
        el.textContent = String(quantity);
      });
    }
  }

  customElements.define('fnd-custom-discount', FndCustomDiscount);
}
